import React, { useEffect, useContext, useState, useRef, useCallback } from 'react';
import { Dimensions, View, Switch } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircle,
  faExclamationTriangle,
  faNote,
  faSms,
} from '@fortawesome/pro-solid-svg-icons';
import { DateTime } from 'luxon';

import {
  Text,
  BoldText,
  Toggles,
  ToggleText,
} from '../../../styles';
import * as colors from '../../../styles/colors';
import { RECORD_USER_ACTIVITY } from '../../../hooks/useSubscriptions/queries';
import MessageInputContext from '../../../contexts/MessageInputContext';
import useSubscriptions from './useSubscriptions';

import { CONVERSATION } from './queries';
import Compose from './Compose';
import UserActivity from './UserActivity';
import DataState from 'app/src/components/DataState';

const dimensions = Dimensions.get('window');
const smallScreen = dimensions.width < 800;

const Container = styled.div`
  flex-direction: ${smallScreen ? 'column' : 'row'};
  flex-grow: 1;
  flex-shrink: 1;
  width: 100%;
  padding-bottom: 10px;
`;

const ContentContainer = styled.div`
  flex-direction: ${smallScreen ? 'column' : 'row'};
  flex-grow: 1;
  flex-shrink: 1;
`;

const ChatContainer = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
`;

const Messages = styled.div`
  padding: 8px;
  padding-bottom: 16px;
  max-height: 150px;
  overflow: auto;
  overflow-anchor: auto;
`;

const IncomingBody = styled.div`
  background-color: #e4e4f0;
  padding: 8px;
  width: 50%;
  border-radius: 6px;
`;

const OutgoingBody = styled(IncomingBody)`
  margin-right: 0px;
  margin-left: auto;
`;

const BodyLinks = styled(View)`
  flex-direction: row;
  margin-top: 8px;
`;

const IncomingInfo = styled(View)`
  margin-top: 10px;
  margin-bottom: 2px;
  flex-direction: row;
  align-items: center;
  column-gap: 8px;
`;

const OutgoingInfo = styled(IncomingInfo)`
  justify-content: flex-end;
`;

const From = styled(Text)`
`;

const Time = styled(Text)`
`;

const DividerContainer = styled(View)`
  margin-top: 10px;
  flex-direction: row;
  column-gap: 10px;
`;

const Line = styled(View)`
  flex-grow: 1;
  margin-top: 8px;
  border-top-style: solid;
  border-top-width: 1px;
  border-top-color: gray;
`;

const INCOMING_SOURCE_COLORS = {
  twilio: '#00e576',
  messenger: '#0081e5',
  fbcomment: '#29C0A7',
};

const messageMedia = {
  width: 'auto',
  maxWidth: '100%'
}

const Divider = ({ text }) => {
  return (
    <DividerContainer>
      <Line />
      <Text>{text}</Text>
      <Line />
    </DividerContainer>
  );
};

const Message = ({ conversation, message }) => {
  const formattedTime = DateTime.fromISO(message.createdAt).
    toLocaleString(DateTime.TIME_WITH_SECONDS);
  const bodyRef = useRef();

  const bodyStyle = {};
  const isImportant = 'important' === message.flags;
  let Info = OutgoingInfo;
  let Body = OutgoingBody;
  let fromName = message.userName || 'User';
  let backgroundColor = colors.elGray;
  let iconColor = backgroundColor;
  let color = '#000000';
  let icon = faCircle;
  let fontWeight = 'normal';

  if (message.isIncoming) {
    Info = IncomingInfo;
    Body = IncomingBody;
    fromName = message.userName || 'User';
    backgroundColor = colors.elRed;
    iconColor = '#e4e4f0';
    color = '#ffffff';
    fontWeight = 'bold';
  } else if ('note' === message.source) {
    color = '#ffffff';
    backgroundColor = '#d15454';
    icon = faNote;
    iconColor = colors.darkText;
  }

  return (
    <View>
      <Body style={{ backgroundColor }} ref={bodyRef}>
        <Text style={{ color, fontWeight }}>
          {getMessageBody(message)}
        </Text>
      </Body>
      <Info
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: message.isIncoming ? 'flex-start' : 'flex-end'
        }}
      >
        <From>{fromName}</From>
        <span style={{width: '5px'}}></span>
        <Time>{formattedTime}</Time>
      </Info>
    </View>
  );

  function getMessageBody(message) {
    return message.body
  }
};

const Conversation = ({conversationId, key, name}) => {
  const conversationContext = useContext(MessageInputContext);
  // const [id, setId] = useState(conversationContext?.currentConversationId);
  const [id, setId] = useState(conversationId);

  useSubscriptions({id});
  const inputContext = useContext(MessageInputContext);

  const { data, loading, refetch, ...queryStatus } = useQuery(
    CONVERSATION, { variables: { id } }
  );

  const [recordUserActivity] = useMutation(RECORD_USER_ACTIVITY);

  useEffect(() => {
    recordUserActivity({
      variables: { conversationId: id, activity: 'open' }
    });

    inputContext?.setCurrentConversationId(id);

    return () => {
      recordUserActivity({
        variables: { conversationId: id, activity: 'close' }
      });
    }
  }, [id]);

  let activeDate;

  const isActive = (message) => (
    message.id === data?.conversation?.messages[data?.conversation?.messages.length - 1].id
  );

  const activeRef = useCallback(element => {
    if (element) {
      element.scrollIntoView();
    }
  }, []);

  return (
    <Container>
      {loading || !data?.conversation ?
        <DataState.Loading /> :
        <View style={{flex: 1}}>
          <ContentContainer smallScreen>
            <ChatContainer>
              <Messages>
                {data?.conversation.messages.map(message => {
                  const messageDate = DateTime.fromISO(message.createdAt).
                    toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);

                  const newMessageDate = messageDate !== activeDate
                  activeDate = messageDate;

                  return (
                    <React.Fragment key={message.id}>
                      {newMessageDate && <Divider text={messageDate} />}
                      <Message
                        conversation={data.conversation}
                        message={message}
                      />
                      <div ref={isActive(message) ?  activeRef : null}></div>
                    </React.Fragment>
                  );
                })}
              </Messages>
            </ChatContainer>
            <UserActivity conversation={data.conversation} />
            <Compose
              conversation={data.conversation}
              refetch={refetch}
              minimal
              key={key}
              name={`conversation-${conversationId}`}
            />
          </ContentContainer>
        </View>
      }
    </Container>
  );
};

export default Conversation;
